import React, { useEffect, useRef, useState } from "react";
import { Bell } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

export default function NotificationBell() {
  const { user, t } = useApp();
  const [items, setItems] = useState([]);
  const seen = useRef(new Set());

  const refresh = async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
      // Fire browser notifications for new unread items (once permission granted)
      if ("Notification" in window && Notification.permission === "granted") {
        data.filter((n) => !n.read && !seen.current.has(n.id)).forEach((n) => {
          seen.current.add(n.id);
          try { new Notification("VOLTIX", { body: n.title, icon: "/favicon.ico" }); } catch { /* ignore */ }
        });
      }
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!user) { setItems([]); return; }
    refresh();
    const id = setInterval(refresh, 30000);
    // Ask permission once per session
    if ("Notification" in window && Notification.permission === "default") {
      // Delay a bit so it doesn't overwhelm on first paint
      setTimeout(() => { try { Notification.requestPermission(); } catch { /* ignore */ } }, 4000);
    }
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!user) return null;
  const unread = items.filter((i) => !i.read).length;

  const markRead = async (nid) => {
    await api.put(`/notifications/${nid}/read`);
    setItems((prev) => prev.map((n) => n.id === nid ? { ...n, read: true } : n));
  };
  const markAll = async () => {
    await api.put("/notifications/read-all");
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="relative p-2 rounded-full hover:bg-muted transition-colors" data-testid="notif-bell" aria-label="Notificações">
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center" data-testid="notif-count">
              {unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto">
        <div className="flex items-center justify-between px-2 py-2">
          <span className="text-xs uppercase tracking-widest text-muted-foreground">Notificações</span>
          {unread > 0 && <Button variant="ghost" size="sm" onClick={markAll} className="text-xs h-auto py-1" data-testid="notif-read-all">Marcar todas</Button>}
        </div>
        <DropdownMenuSeparator />
        {items.length === 0 && <div className="px-3 py-6 text-center text-sm text-muted-foreground">Sem notificações</div>}
        {items.map((n) => (
          <Link
            key={n.id} to={n.link || "#"}
            onClick={() => markRead(n.id)}
            data-testid={`notif-${n.id}`}
            className={`block px-3 py-2.5 hover:bg-muted transition-colors border-l-2 ${n.read ? "border-transparent" : "border-primary bg-primary/5"}`}
          >
            <div className="text-sm font-medium leading-snug">{n.title}</div>
            {n.body && <div className="text-xs text-muted-foreground mt-0.5">{n.body}</div>}
            <div className="text-[10px] text-muted-foreground mt-1">{new Date(n.created_at).toLocaleString("pt-PT")}</div>
          </Link>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
